import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthImageProof } from '@/shared/ui/AuthImage';
import { Dialog } from '@/shared/ui/Dialog';

/**
 * A chore photo-proof thumbnail a grown-up taps to verify before approving. The bytes come from the
 * auth-gated serve endpoint via {@link AuthImage} (the Bearer token rides the shared axios instance).
 * Clicking the thumbnail opens a larger view in the shared focus-trapped {@link Dialog} (a kind
 * lightbox), so the parent can look closely without leaving the queue. Used on both the family and the
 * admin approvals screens.
 */
export function PhotoProofViewer({
  photoKey,
  choreTitle,
  className,
}: {
  photoKey: string;
  /** The chore title, woven into the alt text ("Photo proof for {chore}"). User content — never translated. */
  choreTitle: string;
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const [open, setOpen] = useState(false);
  const alt = t('photoProof.proofAlt', { chore: choreTitle });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('photoProof.viewProof')}
        className={
          className ??
          'block size-16 shrink-0 overflow-hidden rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface'
        }
      >
        <AuthImageProof
          imageKey={photoKey}
          alt={alt}
          className="size-full object-cover"
          fallbackLabel={t('photoProof.unavailable')}
        />
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} title={t('photoProof.viewProof')}>
        <AuthImageProof
          imageKey={photoKey}
          alt={alt}
          className="max-h-[70vh] w-full rounded-card object-contain bg-surface-muted"
          fallbackLabel={t('photoProof.unavailable')}
        />
      </Dialog>
    </>
  );
}
